import * as React from "react";

import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
} from "@/components/ui/Card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

type LoadingSkeletonVariant = "cards" | "rows" | "details" | "form";

interface LoadingSkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: LoadingSkeletonVariant;
  count?: number;
  gridClassName?: string;
  itemClassName?: string;
  showMedia?: boolean;
  showAvatar?: boolean;
  showAction?: boolean;
  /** "details" variant only: number of label/value fields in the top grid */
  fieldCount?: number;
  /** "details" variant only: number of media placeholders (0 to hide) */
  mediaCount?: number;
  /** "details" variant only: number of action buttons at the bottom */
  actionCount?: number;
  /** "details" variant only: show the "Back" button placeholder above the card */
  showBack?: boolean;
  /** "form" variant only: number of label/input field placeholders */
  formFieldCount?: number;
  /** "form" variant only: show a card title placeholder above the fields */
  showTitle?: boolean;
}

function LoadingSkeleton({
  variant = "cards",
  count = 6,
  gridClassName,
  itemClassName,
  showMedia = true,
  showAvatar = true,
  showAction = true,
  fieldCount = 2,
  mediaCount = 2,
  actionCount = 2,
  showBack = true,
  formFieldCount = 4,
  showTitle = true,
  className,
  ...props
}: LoadingSkeletonProps) {
  const items = Array.from({ length: count });

  if (variant === "form") {
    const formFields = Array.from({ length: formFieldCount });

    return (
      <div className={cn("max-w-2xl mx-auto pb-10", className)} {...props}>
        <Card className="border-0!">
          {showTitle && (
            <CardHeader>
              <Skeleton className="h-5 w-40" />
            </CardHeader>
          )}
          <CardContent className="space-y-6">
            {formFields.map((_, index) => (
              <div key={index} className="space-y-2">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-10 w-full rounded-md" />
              </div>
            ))}
            {showAction && <Skeleton className="h-11 w-full rounded-lg" />}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (variant === "details") {
    const fields = Array.from({ length: fieldCount });
    const media = Array.from({ length: mediaCount });
    const actions = Array.from({ length: actionCount });

    return (
      <div className={cn("max-w-3xl mx-auto space-y-6 pb-8", className)} {...props}>
        {showBack && (
          <Button variant="ghost" disabled className="self-start -ml-2 pl-2 opacity-60">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back
          </Button>
        )}

        <Card className="shadow-xs border-0!">
          <CardHeader className="pb-4 border-b border-border/40">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Skeleton className="h-4 w-16 rounded" />
                  <Skeleton className="h-3 w-20" />
                </div>
                <Skeleton className="h-5 w-56" />
              </div>
              <div className="flex flex-col items-end gap-2">
                <Skeleton className="h-6 w-24 rounded-full" />
                <Skeleton className="h-3 w-28" />
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-8 pt-4">
            {fieldCount > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {fields.map((_, index) => (
                  <div key={index} className="space-y-2">
                    <Skeleton className="h-3 w-20" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-2/3" />
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-2">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-24 w-full rounded-lg" />
            </div>

            {mediaCount > 0 && (
              <div className="space-y-3">
                <Skeleton className="h-3 w-28" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {media.map((_, index) => (
                    <Skeleton key={index} className="h-48 w-full rounded-lg" />
                  ))}
                </div>
              </div>
            )}

            {actionCount > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-4 border-t border-border/40">
                {actions.map((_, index) => (
                  <Skeleton key={index} className="h-11 w-full rounded-lg" />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (variant === "rows") {
    return (
      <div className={cn("space-y-3", className)} {...props}>
        {items.map((_, index) => (
          <Card
            key={index}
            className={cn(
              "bg-card/70 shadow-sm border-0!",
              itemClassName,
            )}
          >
            <CardContent className="p-4">
              <div className="flex items-start gap-4">
                {showAvatar ? (
                  <Skeleton className="mt-0.5 size-11 rounded-full" />
                ) : null}
                <div className="min-w-0 flex-1 space-y-2">
                  <Skeleton className="h-4 w-1/3" />
                  <Skeleton className="h-3 w-4/5" />
                  <Skeleton className="h-3 w-2/3" />
                </div>
                {showAction ? <Skeleton className="h-9 w-20 rounded" /> : null}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div
      className={cn("grid gap-4 grid-cols-2", gridClassName, className)}
      {...props}
    >
      {items.map((_, index) => (
        <Card
          key={index}
          className={cn("bg-card/70 border-0!", itemClassName)}
        >
          <CardContent className="space-y-3 p-4">
            {showMedia ? <Skeleton className="h-40 w-full rounded-lg" /> : null}
            <Skeleton className="h-5 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-full" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export { LoadingSkeleton };
